import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const POSITION_STORAGE_KEY = "zoho-salesiq-position";

interface VisitorInfoProps {
  visitorInfo: {
    name: string;
    email: string;
    contactnumber: string;
  };
}

const ZohoSalesIQ: React.FC<VisitorInfoProps> = ({ visitorInfo }) => {
  const location = useLocation();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized) return;

    let cleanupFn: (() => void) | null = null;

    const makeWidgetDraggable = (widget: HTMLElement) => {
      if (widget.dataset.draggableInit === "true") return;
      widget.dataset.draggableInit = "true";

      const savedPosition = localStorage.getItem(POSITION_STORAGE_KEY);
      if (savedPosition) {
        const { bottom, right } = JSON.parse(savedPosition);
        widget.style.setProperty("bottom", bottom, "important");
        widget.style.setProperty("right", right, "important");
        widget.style.setProperty("top", "auto", "important");
        widget.style.setProperty("left", "auto", "important");
      }

      let isDragging = false;
      let startX = 0;
      let startY = 0;
      let initialBottom = 0;
      let initialRight = 0;
      let hasMoved = false;

      const onMouseDown = (e: MouseEvent) => {
        isDragging = true;
        hasMoved = false;
        startX = e.clientX;
        startY = e.clientY;

        const computedStyle = window.getComputedStyle(widget);
        initialBottom = parseInt(computedStyle.bottom) || 0;
        initialRight = parseInt(computedStyle.right) || 0;

        widget.style.cursor = "grabbing";
        widget.style.userSelect = "none";
        e.preventDefault();
        e.stopPropagation();
      };

      const onMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;

        const deltaX = startX - e.clientX;
        const deltaY = e.clientY - startY;

        if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
          hasMoved = true;
        }

        const newBottom = Math.max(
          0,
          Math.min(window.innerHeight - 100, initialBottom - deltaY)
        );
        const newRight = Math.max(
          0,
          Math.min(window.innerWidth - 100, initialRight + deltaX)
        );

        widget.style.setProperty("bottom", `${newBottom}px`, "important");
        widget.style.setProperty("right", `${newRight}px`, "important");
        widget.style.setProperty("top", "auto", "important");
        widget.style.setProperty("left", "auto", "important");
      };

      const onMouseUp = () => {
        if (!isDragging) return;
        isDragging = false;
        widget.style.cursor = "grab";

        const position = {
          bottom: widget.style.bottom,
          right: widget.style.right,
        };
        localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
      };

      const onClick = (e: MouseEvent) => {
        if (hasMoved) {
          e.preventDefault();
          e.stopPropagation();
          hasMoved = false;
        }
      };

      const repositionChatWindow = () => {
        const chatWindow = document.querySelector(
          "#zsiq_chat_wrap"
        ) as HTMLElement;
        if (!chatWindow) return;

        const rect = widget.getBoundingClientRect();
        const isTop = rect.top < window.innerHeight / 2;
        const isLeft = rect.left < window.innerWidth / 2;

        chatWindow.style.setProperty("top", "auto", "important");
        chatWindow.style.setProperty("bottom", "auto", "important");
        chatWindow.style.setProperty("left", "auto", "important");
        chatWindow.style.setProperty("right", "auto", "important");

        if (isTop) {
          chatWindow.style.setProperty(
            "top",
            `${rect.bottom + 20}px`,
            "important"
          );
        } else {
          chatWindow.style.setProperty(
            "bottom",
            `${window.innerHeight - rect.top + 20}px`,
            "important"
          );
        }

        if (isLeft) {
          chatWindow.style.setProperty("left", `${rect.left}px`, "important");
        } else {
          chatWindow.style.setProperty(
            "right",
            `${window.innerWidth - rect.right}px`,
            "important"
          );
        }
      };

      const chatObserver = new MutationObserver(repositionChatWindow);
      chatObserver.observe(document.body, { childList: true, subtree: true });

      widget.style.cursor = "grab";
      widget.style.transition = "none";
      widget.addEventListener("mousedown", onMouseDown, true);
      widget.addEventListener("click", onClick, true);
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);

      cleanupFn = () => {
        widget.removeEventListener("mousedown", onMouseDown, true);
        widget.removeEventListener("click", onClick, true);
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        chatObserver.disconnect();
      };
    };

    const findAndMakeDraggable = () => {
      const selectors = [
        ".zsiq-float",
        ".zsiq-custom-stickercont",
        "#zsiq_float",
      ];

      for (const selector of selectors) {
        const widget = document.querySelector(selector) as HTMLElement;
        if (widget && widget.tagName !== "SCRIPT") {
          makeWidgetDraggable(widget);
          return true;
        }
      }
      return false;
    };

    if (findAndMakeDraggable()) {
      return () => cleanupFn?.();
    }

    const observer = new MutationObserver(() => {
      if (findAndMakeDraggable()) observer.disconnect();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const timers = [500, 1000, 2000].map((delay) =>
      setTimeout(() => {
        if (findAndMakeDraggable()) observer.disconnect();
      }, delay)
    );

    return () => {
      observer.disconnect();
      timers.forEach(clearTimeout);
      cleanupFn?.();
    };
  }, [initialized]);

  useEffect(() => {
    const zohoObject = (window as any).$zoho || {};

    if (Object.keys(zohoObject)?.length === 0) {
      const script = document.createElement("script");
      script.src = `https://salesiq.zohopublic.com/widget?wc=${
        import.meta.env?.VITE_APP_ZOHO_WIDGET_KEY as string
      }`;
      script.async = true;
      script.id = "zsiqscript";
      script.defer = true;
      (window as any).$zoho = {
        salesiq: {
          values: {},
          ready: () => {
            setInitialized(true);
            zohoObject.salesiq.visitor.name(visitorInfo.name);
            zohoObject.salesiq.visitor.email(visitorInfo.email);
            zohoObject.salesiq.visitor.contactnumber(visitorInfo.contactnumber);
          },
        },
        ...zohoObject,
      };

      document.head.appendChild(script);
    }

    return () => {
      const scriptElement = document.getElementById("zsiqscript");
      if (scriptElement) {
        document.head.removeChild(scriptElement);
      }
    };
  }, [visitorInfo.contactnumber, visitorInfo.email, visitorInfo.name]);

  useEffect(() => {
    const zohoObject = (window as any).$zoho;
    if (!zohoObject?.salesiq?.floatbutton) return;

    if (
      location.pathname?.includes("/campaign/") ||
      location.pathname?.includes("/workflow/")
    ) {
      zohoObject?.salesiq?.floatbutton.visible("hide");
    } else {
      zohoObject?.salesiq?.floatbutton.visible("show");
    }
  }, [location.pathname]);

  return null;
};

export default ZohoSalesIQ;
